import Phaser from 'phaser';

import { GameSizeMetrics } from 'src/games/common/phaser/game/scenes/scaled-base-scene';
import { BlocksMainScene } from '../../scenes/blocks-main-scene';
import { Subscription } from 'rxjs';
import { MultiplierControlViewModel } from './multiplier-control-view-model';
import { TextResizer } from '../grid/text-resizer';
import { delay } from 'src/games/common/helper';

export class MultiplierControlView extends Phaser.GameObjects.Container {
  private subscriptionGroup: Subscription;
  private textResizer: TextResizer;

  private viewModel: MultiplierControlViewModel;
  private enableAnimations: boolean;
  private multiplierIcon: Phaser.GameObjects.Image;
  private multiplierLabel: Phaser.GameObjects.Text;

  constructor(scene: BlocksMainScene, x: number, y: number, viewModel: MultiplierControlViewModel) {
    super(scene, x, y);

    this.subscriptionGroup = new Subscription();
    this.textResizer = new TextResizer(scene, this, {
      fontFamily: 'Inter',
      fontSize: '50px',
      fontStyle: 'bold',
      color: '#ffffff',
    });

    this.viewModel = viewModel;
    this.enableAnimations = false;

    this.multiplierIcon = scene.add
      .image(0, 0, viewModel.multiplierIcon.type)
      .setOrigin(0.5, 0.5)
      .setInteractive();
    this.add([this.multiplierIcon]);

    this.multiplierLabel = this.scene.add
      .text(0, 0, '1X', {
        fontFamily: 'Inter',
        fontSize: '40px',
        fontStyle: 'bold',
        color: '#ffffff',
      })
      .setOrigin(0.5, 0.5);
    this.add([this.multiplierLabel]);

    this.setName(MultiplierControlView.name);
    scene.add.existing(this);

    this.subscriptionGroup.add(
      this.viewModel.onGameSetupCompleteTrigger.subscribe(() => {
        this.enableAnimations = true;
      }),
    );
    this.subscriptionGroup.add(
      this.viewModel.showControl$.subscribe((showControl) => {
        this.setVisible(showControl);
      }),
    );
    this.subscriptionGroup.add(
      this.viewModel.onGameSetupStart$.subscribe(() => {
        this.viewModel.onGameSetupStart();
      }),
    );
    this.subscriptionGroup.add(
      this.viewModel.onResetGameScene$.subscribe(() => {
        this.enableAnimations = false;
        this.multiplierLabel.text = '1X';
      }),
    );
    this.subscriptionGroup.add(
      this.viewModel.blocksLogicActionBus$.subscribe((actionBus) => {
        if (!actionBus) {
          return;
        }
        actionBus.on('onMultiplierChanged', async (payload) => {
          if (this.enableAnimations) {
            await delay(1000);
          }
          this.multiplierLabel.text = `${payload.value}X`;
        });
      }),
    );
  }

  override destroy(_fromScene?: boolean | undefined): void {
    this.subscriptionGroup.unsubscribe();

    super.destroy(_fromScene);
  }

  public onResize(gameSizeMetrics: GameSizeMetrics) {
    const layoutConfig = this.viewModel.layoutConfig;
    const calc = this.viewModel.calculateScaleFactor(gameSizeMetrics);

    const cellSz = (layoutConfig.boardWidth - layoutConfig.cellBorder) / 9;
    const innerCellSz = cellSz - layoutConfig.cellBorder;
    const controlWidth = (cellSz * 3 - layoutConfig.cellBorder) * calc.scaleFactor;
    const controlHeight = layoutConfig.controlsHeight * calc.scaleFactor;

    const xPos = (innerCellSz + layoutConfig.cellBorder) * 2 - innerCellSz * 0.5;
    const yPos = controlHeight * 0.5;

    this.setPosition(
      (layoutConfig.hMargin + xPos) * calc.scaleFactor + calc.scaledOffsetX,
      (layoutConfig.controlsMargin + yPos) * calc.scaleFactor,
    );
    this.setSize(controlWidth, controlHeight);

    const iconScale = 0.7;
    this.multiplierIcon.setDisplaySize(controlHeight * iconScale, controlHeight * iconScale);

    const fontSize = this.textResizer.fitText('XXXXXXXXXXXX', controlWidth, 14);
    this.multiplierLabel.setFontSize(fontSize);

    const spacing = controlHeight * 0.3;
    this.multiplierIcon.setX(-spacing * 0.5 - this.multiplierLabel.displayWidth * 0.5);
    this.multiplierLabel.setX(spacing * 0.5 + this.multiplierIcon.displayWidth * 0.5);
  }
}
